import { PropsWithChildren } from 'react';
import { useLocation } from 'react-router';

import { useTerminalDrawerState } from '#/features/terminal/drawer-context';
import { UnifiedRfqProviders } from '#/components/UnifiedRfqs/UnifiedRfqs';
import { UNIFIED_RFQS_ROUTE } from '#/routes';

/**
 * Mounts `UnifiedRfqProviders` once, above both the route tree and the
 * Block Terminal side panel, but only when something downstream actually
 * needs it:
 *
 *   - on the `/rfqs` route (the page consumes the providers), or
 *   - whenever the Block Terminal side panel is open in side-panel mode.
 *
 * Why not mount it inside the `/rfqs` page (as we used to)? The side panel
 * is rendered as a sibling of `<Routes />` in `App.tsx`, not as a
 * descendant, so a page-level wrap can't be shared with it via React
 * context — the panel would mount its own providers, spawning a second
 * `DrfqNotificationsService` and firing every fill toast twice.
 *
 * Why not mount it unconditionally? Then any user on `/paradex` or
 * `/futurespreads` would receive RFQ/OB notifications they never opted
 * into. Scoping to "RFQ route OR side panel open" keeps the main flow
 * unchanged.
 */
export default function ScopedUnifiedRfqProviders({
  children,
}: PropsWithChildren) {
  const { pathname } = useLocation();
  const { isOpen, pinMode } = useTerminalDrawerState();

  const isOnRfqsRoute = pathname === UNIFIED_RFQS_ROUTE;
  const sidePanelActive = isOpen && pinMode === 'side-panel';

  // Fragment is needed so the function returns a JSX element rather than
  // raw ReactNode (which is not a valid component return type under
  // strict TS settings).
  if (!isOnRfqsRoute && !sidePanelActive) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }
  return <UnifiedRfqProviders>{children}</UnifiedRfqProviders>;
}
